<template>
  <v-dialog
    v-model="dialog"
    max-width="480"
  >
    <template #activator="{ props: activatorProps }">
      <v-btn
        v-bind="activatorProps"
        variant="outlined"
        size="small"
      >
        {{
          teamId
            ? $t('settings.crm.multi-company-parent.change-team')
            : $t('settings.crm.multi-company-parent.assign-team')
        }}
      </v-btn>
    </template>
    <v-card>
      <v-card-title>
        {{ $t('settings.crm.multi-company-parent.assign-dialog-title', { company: companyName }) }}
      </v-card-title>
      <v-card-text>
        <v-autocomplete
          v-model="selectedTeamId"
          :items="teamItems"
          :label="$t('settings.crm.multi-company-parent.team-label')"
          item-title="name"
          item-value="id"
          :loading="teamsLoading"
          variant="outlined"
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          variant="text"
          @click="dialog = false"
          >{{ $t('labels.cancel') }}</v-btn
        >
        <v-btn
          color="primary"
          :loading="saving"
          :disabled="!selectedTeamId"
          @click="save"
        >
          {{ $t('labels.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { useMutation, useQuery } from '@vue/apollo-composable'
import { computed, inject, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { showSnackbarMessage } from '@/lib/snackbarMessages'

import PROPSTACK_TEAMS from './queries/PropstackTeams.gql'
import SAVE_PROPSTACK_CHILD_TEAM_ASSIGNMENT from './queries/SavePropstackChildTeamAssignment.gql'

const props = defineProps<{
  companyName: string
  childCompanyId: string
  teamId?: string | null
}>()

const emit = defineEmits<{ saved: [] }>()

const { t } = useI18n()
const auth = inject<{ user?: { companyId: string } }>('auth')
const parentCompanyId = computed(() => auth?.user?.companyId ?? '')

const dialog = ref(false)
const selectedTeamId = ref<string | null>(null)
const saving = ref(false)

watch(dialog, (open) => {
  if (open) selectedTeamId.value = props.teamId ? String(props.teamId) : null
})

const { result: teamsResult, loading: teamsLoading } = useQuery(PROPSTACK_TEAMS, () => ({
  companyId: parentCompanyId.value,
}))
const teamItems = computed(() => teamsResult.value?.propstackTeams ?? [])

const { mutate: saveAssignment } = useMutation(SAVE_PROPSTACK_CHILD_TEAM_ASSIGNMENT)

async function save() {
  if (!selectedTeamId.value) return
  saving.value = true
  try {
    await saveAssignment({
      input: {
        parentCompanyId: parentCompanyId.value,
        childCompanyId: props.childCompanyId,
        teamId: String(selectedTeamId.value),
      },
    })
    dialog.value = false
    emit('saved')
    showSnackbarMessage('success', t('alerts.settings.crm.success'))
  } catch {
    showSnackbarMessage('error', t('alerts.settings.crm.error'))
  } finally {
    saving.value = false
  }
}
</script>
